/**
 * Pure helpers for the Financing page (`design/Syntra/financing.html`).
 *
 * Financing is sourced from `property_setting` strategy blobs — the same model
 * the Properties cards and the find-detail panels use. For each owned property
 * we resolve the *active* loan (short-term hard money, long-term refinance, or
 * conventional) the same way the design's `loanInfo()` does:
 *
 *   - `loanViewMeta(strategy, view)` → section label, badge, term unit, defaults.
 *   - `computeOwnedLoanInfo(...)`    → the active loan for the table + KPIs.
 *   - `aggregateFinancingKpis(...)`  → page-level totals (matches the KPI row).
 *   - `computeLoanDraftMetrics(...)` → the editor drawer's live "Computed" tiles.
 *   - date helpers (`nextEmiDate`, `maturityRemaining`, `maturityDateFrom`).
 */
import { amortizedEmi, interestOnlyMonthly, termInMonths } from "@/lib/calculators";
import type {
  BrrrrSettings,
  FlipSettings,
  LoanInputs,
  RentalSettings,
  Strategy,
} from "@/types/pipeline";

export type LoanBadgeTone = "hard" | "refi" | "conv" | "cash" | "other";

export interface LoanBadge {
  tone: LoanBadgeTone;
  label: string;
}

export const CASH_BADGE: LoanBadge = { tone: "cash", label: "All Cash" };

/** Which financing step the user is viewing for an owned property. */
export type LoanView = "short" | "long";

export interface LoanViewMeta {
  /** Section title, e.g. "Short-Term (Hard Money) Loan". */
  sectionLabel: string;
  badge: LoanBadge;
  /** Whether the loan's term is expressed in months (short) or years (long). */
  termUnit: "months" | "years";
  /** Amortizing (conventional/refi) vs interest-only with a balloon (hard money). */
  isAmortizing: boolean;
  defaults: { ltv: number; rate: number; term: number; points: number };
}

const HARD_MONEY: LoanViewMeta = {
  sectionLabel: "Short-Term (Hard Money) Loan",
  badge: { tone: "hard", label: "Hard Money" },
  termUnit: "months",
  isAmortizing: false,
  defaults: { ltv: 85, rate: 11, term: 12, points: 2 },
};

/**
 * Strategy + view → loan presentation/defaults. Mirrors `loanTypeMeta()` in the
 * design mockup. Rental always shows a conventional long-term loan; Flip always
 * a hard-money short-term loan; BRRRR toggles between the two.
 */
export function loanViewMeta(strategy: Strategy, view: LoanView): LoanViewMeta {
  if (strategy === "Rental") {
    return {
      sectionLabel: "Long-Term (Conventional) Loan",
      badge: { tone: "conv", label: "Conventional" },
      termUnit: "years",
      isAmortizing: true,
      defaults: { ltv: 80, rate: 7, term: 30, points: 1 },
    };
  }
  if (strategy === "Flip") return HARD_MONEY;
  // BRRRR
  if (view === "long") {
    return {
      sectionLabel: "Refinance Loan (against ARV)",
      badge: { tone: "refi", label: "Refinance" },
      termUnit: "years",
      isAmortizing: true,
      defaults: { ltv: 75, rate: 7, term: 30, points: 1 },
    };
  }
  return HARD_MONEY;
}

/**
 * Whether a stored loan blob carries any real terms.
 *
 * A missing column (`brrrr_long_term: null`), an empty blob (`{}`), and an
 * all-zero blob all read as "no loan captured yet". The editor seeds those slots
 * as an empty (all-zero) form rather than the strategy defaults, so the drawer
 * never shows loan figures the deal doesn't actually have.
 */
export function hasLoanTerms(
  loan: Partial<LoanInputs> | null | undefined,
): boolean {
  if (!loan) return false;
  return Boolean(
    Number(loan.ltv) ||
      Number(loan.rate) ||
      Number(loan.points) ||
      Number(loan.term) ||
      Number(loan.term_months) ||
      loan.loan_date,
  );
}

/** Views available for a strategy — BRRRR has both; Flip short-only; Rental long-only. */
export function availableViews(strategy: Strategy): LoanView[] {
  if (strategy === "BRRRR") return ["short", "long"];
  if (strategy === "Flip") return ["short"];
  return ["long"];
}

/** The basis a loan's LTV applies to: ARV for a BRRRR refinance, else purchase. */
export function baseForView(
  strategy: Strategy,
  view: LoanView,
  purchase: number,
  arv: number,
): number {
  return strategy === "BRRRR" && view === "long" ? arv : purchase;
}

/** ARV from settings, falling back to the design's purchase × 1.28 heuristic. */
export function resolveArv(
  purchase: number,
  settingsArv: number | null | undefined,
): number {
  return Number(settingsArv) || Math.round(purchase * 1.28);
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Active-loan resolution for the table + KPIs
 * ────────────────────────────────────────────────────────────────────────── */

/** Just the strategy financing blobs `computeOwnedLoanInfo` reads. */
export interface FinancingSettings {
  purchase_price?: number | null;
  after_repair_value?: number | null;
  flip?: FlipSettings;
  brrrr?: BrrrrSettings;
  rental?: RentalSettings;
}

export interface OwnedLoanInfo {
  /** False = held all-cash (financing disabled), no loan figures. */
  financed: boolean;
  lender: string;
  view: LoanView;
  badge: LoanBadge;
  loanAmount: number;
  /** Annual interest rate (percent points). */
  rate: number;
  termMonths: number;
  monthly: number;
  loanDate: string | null;
}

/**
 * Per-property financing metadata, sourced from the `loans` table (NOT the
 * property_setting blobs). `loan_type` encodes the active slot.
 */
export interface LoanMeta {
  is_enabled: boolean;
  lender_name: string | null;
  /** 'hard_money' = short; 'refinance' / 'conventional' = long. */
  loan_type: string;
}

/** The active loan view, derived from strategy + the loan's `loan_type`. */
export function activeView(
  strategy: Strategy,
  loanType?: string | null,
): LoanView {
  if (strategy === "Flip") return "short";
  if (strategy === "Rental") return "long";
  return loanType === "refinance" ? "long" : "short";
}

/**
 * Resolve the active loan for one owned property — the figures the table row +
 * KPI roll-up display. Mirrors the design's `loanInfo()`.
 */
export function computeOwnedLoanInfo(args: {
  settings: FinancingSettings | null;
  strategy: Strategy | null;
  fallbackPurchase: number | null | undefined;
  /** Financing metadata from the `loans` table. Absent ⇒ default financed. */
  loan?: LoanMeta | null;
}): OwnedLoanInfo {
  const strategy: Strategy = args.strategy ?? "BRRRR";
  const s = args.settings;
  const purchase = Number(s?.purchase_price) || Number(args.fallbackPurchase) || 0;
  const arv = resolveArv(purchase, s?.after_repair_value);

  const loanMeta = args.loan ?? null;
  // No loans row yet ⇒ default to financed (scenario terms); an explicit
  // all-cash deal is a row with is_enabled = false.
  const enabled = loanMeta ? loanMeta.is_enabled : true;
  const lender = (loanMeta?.lender_name ?? "").trim();
  const view = activeView(strategy, loanMeta?.loan_type);
  const meta = loanViewMeta(strategy, view);

  if (!enabled) {
    return {
      financed: false,
      lender,
      view,
      badge: CASH_BADGE,
      loanAmount: 0,
      rate: 0,
      termMonths: 0,
      monthly: 0,
      loanDate: null,
    };
  }

  let loan: LoanInputs | undefined;
  if (strategy === "Rental") loan = s?.rental?.long_term;
  else if (strategy === "Flip") loan = s?.flip?.hard_money;
  else loan = view === "long" ? s?.brrrr?.long_term : s?.brrrr?.hard_money;

  // The refi LTV (against ARV) now lives on brrrr.long_term.ltv like any other loan.
  const ltv = Number(loan?.ltv) || meta.defaults.ltv;
  const rate = Number(loan?.rate) || meta.defaults.rate;
  // Canonical months: prefers loan.term_months, else the slot-implied default.
  const termMonths = loan
    ? termInMonths(loan, meta.termUnit) ||
      (meta.termUnit === "years" ? meta.defaults.term * 12 : meta.defaults.term)
    : meta.termUnit === "years"
      ? meta.defaults.term * 12
      : meta.defaults.term;
  const base = baseForView(strategy, view, purchase, arv);
  const loanAmount = Math.round(base * (ltv / 100));
  // Hard money (`isAmortizing: false`) carries interest only and balloons the
  // principal — amortizing it would report a ~10× monthly payment on a 12-month
  // term and inflate the page's Total Monthly Payment KPI by the same factor.
  const monthly = meta.isAmortizing
    ? amortizedEmi(loanAmount, rate, termMonths)
    : interestOnlyMonthly(loanAmount, rate);

  return {
    financed: true,
    lender,
    view,
    badge: meta.badge,
    loanAmount,
    rate,
    termMonths,
    monthly,
    loanDate: loan?.loan_date ?? null,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Live editor (drawer) metrics — the design's "Computed" tiles
 * ────────────────────────────────────────────────────────────────────────── */

export interface LoanDraftInput {
  /** Principal in dollars. */
  loanAmount: number;
  /** Annual interest rate in percent points (7 = 7%). */
  annualRatePct: number;
  termMonths: number;
  /** Origination points in percent points (2 = 2%). */
  points: number;
  /** Amortizing (no balloon) vs interest-only. From {@link loanViewMeta}. */
  isAmortizing: boolean;
}

export interface LoanDraftMetrics {
  loanAmount: number;
  /**
   * The actual monthly payment: an amortizing EMI, or the interest-only carry
   * when the loan balloons. Named `monthlyEmi` for back-compat — read
   * `isAmortizing` before labelling it "EMI" in the UI.
   */
  monthlyEmi: number;
  originationFee: number;
  monthlyInterest: number;
  totalInterest: number;
  totalLoanCost: number;
  /** Full principal for interest-only loans; 0 when amortizing. */
  balloonPayment: number;
  /** Echoed from the input so consumers can label the payment correctly. */
  isAmortizing: boolean;
}

/**
 * Mirrors `updateDrawerComputed()` in the design mockup: amortizing loans cost
 * `EMI × term`; interest-only loans cost `origination + total interest + the
 * balloon principal`.
 *
 * `monthlyEmi` follows the same split. An amortizing payment on a balloon loan
 * would repay the principal over the term AND again at the balloon, and callers
 * multiply this by a hold period (see `FinancingHoldingCosts`), so the carry is
 * the only correct figure for hard money.
 */
export function computeLoanDraftMetrics(
  input: LoanDraftInput,
): LoanDraftMetrics {
  const loanAmount = Math.max(0, input.loanAmount);
  const termMonths = Math.max(0, input.termMonths);
  const originationFee = loanAmount * (input.points / 100);
  const monthlyInterest = loanAmount * (input.annualRatePct / 100 / 12);
  const totalInterest = monthlyInterest * termMonths;
  const monthlyEmi = input.isAmortizing
    ? amortizedEmi(loanAmount, input.annualRatePct, termMonths)
    : monthlyInterest;
  const balloonPayment = input.isAmortizing ? 0 : loanAmount;
  const totalLoanCost = input.isAmortizing
    ? monthlyEmi * termMonths
    : originationFee + totalInterest + loanAmount;
  return {
    loanAmount,
    monthlyEmi,
    originationFee,
    monthlyInterest,
    totalInterest,
    totalLoanCost,
    balloonPayment,
    isAmortizing: input.isAmortizing,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Date helpers
 * ────────────────────────────────────────────────────────────────────────── */

/** Loan date + term, as a `Date` (local noon-safe), or null when incomputable. */
export function maturityDateFrom(
  loanDateIso: string | null | undefined,
  termMonths: number | null | undefined,
): Date | null {
  const term = Number(termMonths) || 0;
  if (!loanDateIso || term <= 0) return null;
  const d = new Date(loanDateIso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + term);
  return d;
}

/**
 * Next monthly EMI date — same day-of-month as the loan date, the next
 * occurrence at or after today. Returns null when we can't compute it.
 */
export function nextEmiDate(loanDateIso: string | null | undefined): Date | null {
  if (!loanDateIso) return null;
  const start = new Date(loanDateIso + "T00:00:00");
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = start.getDate();
  let d = new Date(today.getFullYear(), today.getMonth(), day);
  if (d < today) d = new Date(today.getFullYear(), today.getMonth() + 1, day);
  return d;
}

/**
 * Time remaining until the loan matures. If no loan date is set, assume the
 * full term still remains (matches design behavior).
 */
export function maturityRemaining(
  loanDateIso: string | null | undefined,
  termMonths: number | null | undefined,
): string {
  const term = Number(termMonths) || 0;
  if (term <= 0) return "—";
  let monthsLeft = term;
  if (loanDateIso) {
    const start = new Date(loanDateIso + "T00:00:00");
    if (!Number.isNaN(start.getTime())) {
      const mat = new Date(start);
      mat.setMonth(mat.getMonth() + term);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let m =
        (mat.getFullYear() - today.getFullYear()) * 12 +
        (mat.getMonth() - today.getMonth());
      if (mat.getDate() < today.getDate()) m -= 1;
      if (m <= 0) return "Matured";
      monthsLeft = m;
    }
  }
  const y = Math.floor(monthsLeft / 12);
  const mo = monthsLeft % 12;
  const parts: string[] = [];
  if (y) parts.push(`${y}y`);
  if (mo) parts.push(`${mo}m`);
  return `${parts.join(" ") || "0m"} left`;
}

export function fmtMaturityDate(date: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/* ────────────────────────────────────────────────────────────────────────── *
 * KPI roll-up across owned properties
 * ────────────────────────────────────────────────────────────────────────── */

export interface FinancingKpis {
  totalLoanBalance: number;
  totalMonthlyPayment: number;
  /** Weighted avg interest rate (by balance). 0 when no financed loans. */
  avgRate: number;
  /** Number of properties with an active financed loan. */
  financedCount: number;
  /** Total owned properties (denominator for "X / Y financed"). */
  ownedCount: number;
}

export function aggregateFinancingKpis(infos: OwnedLoanInfo[]): FinancingKpis {
  let totalLoanBalance = 0;
  let totalMonthlyPayment = 0;
  let weightedRateNumerator = 0;
  let financedCount = 0;

  for (const info of infos) {
    if (!info.financed || info.loanAmount <= 0) continue;
    financedCount += 1;
    totalLoanBalance += info.loanAmount;
    totalMonthlyPayment += info.monthly;
    weightedRateNumerator += info.rate * info.loanAmount;
  }

  return {
    totalLoanBalance,
    totalMonthlyPayment,
    avgRate:
      totalLoanBalance > 0 ? weightedRateNumerator / totalLoanBalance : 0,
    financedCount,
    ownedCount: infos.length,
  };
}
